import React from "react";

import { AccordionGroup, Alert, Stack, Text } from "@origin-digital/ods-core";

import {
  IIBatterySizePrice,
  IISolarBatteryOutlookResponse,
} from "@/contract/generated/electrify-v1";
import { solarBatteryOutlook as content } from "../content.json";
import { Outcome } from "./Outcome";
import { BatteryAccordion } from "../../SharedComponents/BatteryAccordion";
import { StyledHeading } from "@/components";

export const Battery = ({
  solarBatterySystem,
}: {
  solarBatterySystem: IISolarBatteryOutlookResponse;
}) => {
  const {
    batteryData,
    percentageDailyConsumption,
    batterySize: bSize,
  } = solarBatterySystem;

  const isNoCover =
    percentageDailyConsumption && percentageDailyConsumption < 70;
  const isNotEnoughCover =
    percentageDailyConsumption &&
    percentageDailyConsumption >= 70 &&
    percentageDailyConsumption < 100;

  const isBatterySuported = !isNoCover && !isNotEnoughCover;

  const nonDefaultBatteryData = batteryData?.filter(
    (battery) => battery.batterySize !== bSize
  );
  const defaultBatteryData = batteryData?.filter(
    (battery) => battery.batterySize === bSize
  );

  const sortedNonDefaultBatteries = nonDefaultBatteryData
    ? nonDefaultBatteryData.sort(
        (a, b) => Number(b.batterySize) - Number(a.batterySize)
      )
    : [];
  const batteries = defaultBatteryData
    ? defaultBatteryData?.concat(sortedNonDefaultBatteries)
    : [];
  const batterySizes = batteries.map((b) => Number(b.batterySize));
  const biggestBatterySize = Math.max(...batterySizes);
  const defaultBatterySize = Number(bSize);

  const batteryAccordions = batteries.map((b: IIBatterySizePrice, index) => {
    const { batterySize } = b;
    const accordionId = `${batterySize}-${index}-kw-battery-accordion`;
    const defaultExpanded = Number(batterySize) === defaultBatterySize;
    return (
      <BatteryAccordion
        key={accordionId}
        accordionId={accordionId}
        defaultExpanded={defaultExpanded}
        batterySizePrice={b}
      />
    );
  });

  return (
    <Stack space="large">
      <StyledHeading tone="primaryB">
        {content.batteryOutcome.heading}
      </StyledHeading>
      {isBatterySuported ? (
        <Stack space="large">
          <Outcome
            biggestBatterySize={defaultBatterySize}
            isBatterySuported={isBatterySuported}
          />

          <Text tone="neutralDark">
            {content.batteryOutcome.batteryCostText}
          </Text>

          <AccordionGroup>{batteryAccordions}</AccordionGroup>

          <Alert
            tone="info"
            title={content.batteryOutcome.keepInMindAlert.title}
          >
            <Stack space="xsmall">
              <Text tone="neutralDark" variant="body-small">
                {content.batteryOutcome.keepInMindAlert.installationCostsText}
              </Text>
              <Text tone="neutralDark" variant="body-small">
                {
                  content.batteryOutcome.keepInMindAlert
                    .provideDetailedQuoteText
                }
              </Text>
            </Stack>
          </Alert>
        </Stack>
      ) : (
        <Stack space="large">
          <Outcome
            biggestBatterySize={biggestBatterySize}
            isBatterySuported={isBatterySuported}
          />
          <Alert
            tone="info"
            title={content.batteryOutcome.solarGenerationNeeded.heading}
          >
            <Text tone="neutralDark" variant="body-small">
              {content.batteryOutcome.solarGenerationNeeded.talkToExperts}
            </Text>
          </Alert>
        </Stack>
      )}
      <Text tone="neutralDark" align="center">
        {content.batteryOutcome.notLockingAnythingText}
      </Text>
    </Stack>
  );
};
